// ────────────────────────────────────────────────────────────────────
// SettingsScreen — Phase 8 placeholder.
//
// Sections: profile · tenant · notifications · team · about
// All read-only display except local notification toggles.
// Real write APIs come in production polish.
// ────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, User, Users, Building2, Info, Database, Download } from "lucide-react";
import {
  getCachedUser, getActiveTenant, apiClient, STAFF_ROLES, downloadExportBundle,
} from "@/lib/api";
import type { UserOut, UserUpdate } from "@/lib/types";

type SectionKey = "profile" | "tenant" | "notify" | "team" | "data" | "about";

const SECTIONS: { key: SectionKey; label: string; icon: typeof User }[] = [
  { key: "profile", label: "User profile",    icon: User },
  { key: "tenant",  label: "Tenant",          icon: Building2 },
  { key: "notify",  label: "Notifications",   icon: Bell },
  { key: "team",    label: "Team members",    icon: Users },
  { key: "data",    label: "Your data",       icon: Database },
  { key: "about",   label: "About",           icon: Info },
];

export default function SettingsScreen(): JSX.Element {
  const [section, setSection] = useState<SectionKey>("profile");
  const user = getCachedUser();

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden" style={{ background: "var(--paper)" }}>
      <div className="px-8 py-6 border-b" style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase mb-2"
             style={{ fontSize: 10.5, letterSpacing: ".24em", color: "var(--accent-deep)", fontWeight: 600 }}>
          / SYSTEM CONFIGURATION
        </div>
        <h1 className="font-display font-medium m-0"
            style={{ fontSize: 32, color: "var(--ink-1)", letterSpacing: "-.02em" }}>
          Settings
        </h1>
      </div>

      <div className="flex-1 grid min-h-0" style={{ gridTemplateColumns: "240px 1fr" }}>
        {/* Sidebar */}
        <nav className="border-r overflow-y-auto py-3"
             style={{ borderColor: "var(--hairline)" }}>
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = section === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                className="w-full text-left px-5 py-2.5 flex items-center gap-2.5 font-mono uppercase transition-colors"
                style={{
                  fontSize: 10.5, letterSpacing: ".22em",
                  color: active ? "var(--accent-deep)" : "var(--ink-3)",
                  background: active ? "rgba(var(--accent-rgb) / 0.06)" : "transparent",
                  borderLeft: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                }}
              >
                <Icon size={12} />
                {s.label}
              </button>
            );
          })}
        </nav>

        {/* Body */}
        <main className="overflow-y-auto p-8">
          <div className="max-w-3xl">
            {section === "profile" && <ProfileSection user={user} />}
            {section === "tenant"  && <TenantSection />}
            {section === "notify"  && <NotifySection />}
            {section === "team"    && <TeamSection />}
            {section === "data"    && <DataSection />}
            {section === "about"   && <AboutSection />}
          </div>
        </main>
      </div>
    </div>
  );
}

function ProfileSection({ user }: { user: ReturnType<typeof getCachedUser> }): JSX.Element {
  return (
    <Section title="User profile" desc="Personal account info shown in the top bar and audit trails.">
      <Field label="Username"      value={user?.username ?? "—"} />
      <Field label="Full name"     value={user?.full_name || "—"} />
      <Field label="Role"          value={user?.role ?? "—"} />
      <Field label="Cert no"       value={user?.cert_no || "—"} />
      <Field label="Active"        value={user?.is_active ? "Yes" : "No"} />
    </Section>
  );
}

function TenantSection(): JSX.Element {
  // Real organisation name — was hardcoded "XZYMODS"/"Thailand", which showed
  // the provider's name to every customer. The codes/regulator below ARE
  // platform defaults (not per-tenant), so they're labelled as such.
  const current = getActiveTenant();
  const { data: clients } = useQuery({ queryKey: ["clients"], queryFn: () => apiClient.clients(), staleTime: 60_000 });
  const org = clients?.find((c) => String(c.id) === current)?.name
    ?? (clients?.length === 1 ? clients[0].name : undefined);
  return (
    <Section title="Tenant" desc="Organisation context for compliance reporting.">
      <Field label="Organisation"     value={org || "—"} />
      <Field label="Reporting period" value={`FY${new Date().getFullYear()}`} />
      <Field label="Default codes"    value="API 510 · 570 · 653 · ASME V · AWS D1.1" hint="platform default" />
      <Field label="Regulator"        value="DOEB · MoE Notification 2560 (2017)" hint="platform default" />
    </Section>
  );
}

function NotifySection(): JSX.Element {
  return (
    <Section title="Notifications"
             desc="When XZYNEXA should ping you in the inbox or by email.">
      <Toggle label="Critical finding (CR > t-min breach)" defaultChecked tone="crit" />
      <Toggle label="Warning (t-current approaching t-min)" defaultChecked tone="warn" />
      <Toggle label="Inspection due within 30 days" defaultChecked />
      <Toggle label="Engineer review queued > 7 days" defaultChecked />
      <Toggle label="Daily digest email" />
      <Toggle label="Slack webhook (critical only)" />
    </Section>
  );
}

export function TeamSection(): JSX.Element {
  const me = getCachedUser();
  const qc = useQueryClient();
  const isAdmin = me?.role === "admin";
  const q = useQuery({
    queryKey: ["users"],
    queryFn: () => apiClient.listUsers(),
    enabled: isAdmin,
  });
  const m = useMutation({
    mutationFn: (v: { id: number; patch: UserUpdate }) => apiClient.updateUser(v.id, v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  if (!isAdmin) {
    return (
      <Section title="Team members" desc="Users in your organization with portal access.">
        <div className="mt-2 p-6 text-center font-mono uppercase"
             style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)",
                      background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
          Admin access required
        </div>
      </Section>
    );
  }

  const users = q.data ?? [];
  // listUsers() returns every user across ALL tenants (User rows aren't tenant-scoped),
  // so filter to staff-only: sign-off authority is staff-only, and this avoids dumping
  // every tenant's client rows into one flat table.
  const staffUsers = users.filter((u) => (STAFF_ROLES as readonly string[]).includes(u.role));
  const unreachable = staffUsers.filter((u) => !(u.email ?? "").trim()).length;
  const th = { fontSize: 9, letterSpacing: ".18em", color: "var(--ink-4)", textAlign: "left" as const, padding: "6px 8px" };
  const td = { fontSize: 12, color: "var(--ink-1)", padding: "8px", borderTop: "1px solid var(--hairline)" };

  return (
    <Section title="Team members"
             desc="Grant Level III sign-off authority to qualified staff, and set where the weekly inspection-due digest is delivered.">
      {q.isLoading ? (
        <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>Loading…</div>
      ) : staffUsers.length === 0 ? (
        <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>No staff users.</div>
      ) : (
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr className="font-mono uppercase">
              <th style={th}>Username</th><th style={th}>Name</th><th style={th}>Role</th>
              <th style={th}>Cert no</th><th style={th}>Digest email</th><th style={th}>Level III sign-off</th>
            </tr>
          </thead>
          <tbody>
            {staffUsers.map((u) => (
              <tr key={u.id}>
                <td className="font-mono" style={td}>{u.username}</td>
                <td className="font-sans" style={td}>{u.full_name || "—"}</td>
                <td className="font-mono" style={td}>{u.role}</td>
                <td className="font-mono" style={{ ...td, color: "var(--ink-3)" }}>{u.cert_no || "—"}</td>
                <td style={td}>
                  <EmailCell user={u} disabled={m.isPending}
                             onCommit={(email) => m.mutate({ id: u.id, patch: { email } })} />
                </td>
                <td style={td}>
                  <SignoffToggle on={u.can_sign_off} disabled={m.isPending} who={u.username}
                                 onToggle={() => m.mutate({ id: u.id, patch: { can_sign_off: !u.can_sign_off } })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* The digest is a scheduled job with no UI of its own: if an address is
          missing the send is simply skipped, silently. State the miss here —
          this table is the only place the gap is fixable. */}
      {unreachable > 0 && (
        <div className="mt-2 font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
          {`${unreachable} of ${staffUsers.length} staff have no address — the weekly inspection-due digest reaches nobody at those accounts.`}
        </div>
      )}
      {m.isError && (
        <div role="alert" className="mt-2 font-mono" style={{ fontSize: 11, color: "var(--err)" }}>
          Update failed — please try again.
        </div>
      )}
    </Section>
  );
}

export function DataSection(): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function onExport(): Promise<void> {
    setBusy(true);
    setError("");
    try {
      await downloadExportBundle();
    } catch {
      setError("Export failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Your data"
      desc="Everything you have put into XZYNEXA is yours. Export it at any time — no request needed."
    >
      <p className="text-sm mb-4 max-w-2xl" style={{ color: "var(--ink-2)" }}>
        A ZIP containing one CSV per data table (assets, CMLs, thickness
        readings, inspections, reviews) plus every file you have uploaded, and a
        manifest listing exactly what it holds.
      </p>
      <button
        type="button"
        onClick={onExport}
        disabled={busy}
        className="flex items-center gap-2.5 px-4 py-2.5 font-mono uppercase transition-colors"
        style={{
          fontSize: 10.5, letterSpacing: ".22em",
          color: busy ? "var(--ink-4)" : "var(--accent-deep)",
          background: "var(--paper-2)",
          border: `1px solid ${busy ? "var(--hairline)" : "var(--accent)"}`,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        <Download size={12} />
        {busy ? "Preparing your export…" : "Export all my data"}
      </button>
      {error && (
        <div role="alert" className="mt-2 font-mono" style={{ fontSize: 11, color: "var(--err)" }}>
          {error}
        </div>
      )}
    </Section>
  );
}

function AboutSection(): JSX.Element {
  return (
    <Section title="About XZYNEXA" desc="Build + version info.">
      <Field label="Version"    value="0.1 · MVP" />
      <Field label="Built on"   value="React · TypeScript · Vite · Tailwind · TanStack Query · Leaflet" />
      <Field label="Backend"    value="FastAPI · SQLAlchemy · SQLite" />
      <Field label="Support"    value="support@xzymods.com" />
      <Field label="Compliance" value="API 510/570/653 · ASME V/VIII · AWS D1.1 · DOEB 2560" />
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
function Section({ title, desc, children }: {
  title: string; desc: string; children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <h2 className="font-display font-medium m-0"
          style={{ fontSize: 24, color: "var(--ink-1)", letterSpacing: "-.015em" }}>
        {title}
      </h2>
      <p className="text-sm mt-2 mb-6 max-w-2xl" style={{ color: "var(--ink-3)" }}>
        {desc}
      </p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element {
  return (
    <div className="grid items-center gap-4 py-2.5"
         style={{ gridTemplateColumns: "180px 1fr",
                  borderBottom: "1px solid rgba(214,220,228,0.5)" }}>
      <div className="font-mono uppercase"
           style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--ink-4)" }}>
        {label}
      </div>
      <div className="font-mono flex items-baseline gap-2" style={{ fontSize: 12, color: "var(--ink-1)" }}>
        {value}
        {hint && <span style={{ fontSize: 9, color: "var(--ink-4)", letterSpacing: ".1em" }}>· {hint}</span>}
      </div>
    </div>
  );
}

/**
 * One staff member's digest address, edited in place. Commits on blur or Enter
 * and only when the value actually changed — a click-through of the roster must
 * not fire a PATCH per row. Escape restores the stored value.
 *
 * The address is validated server-side (schemas.UserUpdate._email_shape): the
 * digest joins every staff address into one To: header, so a single malformed
 * one fails the whole send. A rejected value surfaces through the shared
 * mutation error above rather than being guessed at here.
 */
function EmailCell({ user, disabled, onCommit }: {
  user: UserOut; disabled: boolean; onCommit: (email: string) => void;
}): JSX.Element {
  const stored = user.email ?? "";
  const [value, setValue] = useState(stored);
  // A successful commit refetches the roster; re-seed so the cell shows what
  // the server actually stored (it strips whitespace), not what was typed.
  useEffect(() => setValue(stored), [stored]);

  return (
    <input
      type="email"
      aria-label={`Email for ${user.username}`}
      value={value}
      disabled={disabled}
      placeholder="no address"
      spellCheck={false}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => { if (value.trim() !== stored) onCommit(value.trim()); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") setValue(stored);
      }}
      className="font-mono w-full"
      style={{
        fontSize: 11.5, padding: "3px 6px", color: value ? "var(--ink-1)" : "var(--ink-3)",
        background: "var(--paper-2)", border: "1px solid var(--hairline)", borderRadius: 2,
      }}
    />
  );
}

function SignoffToggle({ on, disabled, onToggle, who }: {
  on: boolean; disabled: boolean; onToggle: () => void; who: string;
}): JSX.Element {
  return (
    <button type="button" onClick={onToggle} disabled={disabled}
            aria-pressed={on} aria-label={`Sign-off authority for ${who}`}
            className="relative w-9 h-5 rounded-full transition-colors"
            style={{ background: on ? "var(--accent)" : "#D6DCE4",
                     opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
            style={{ left: on ? 18 : 2 }} />
    </button>
  );
}

function Toggle({ label, defaultChecked, tone }: {
  label: string; defaultChecked?: boolean; tone?: "crit" | "warn";
}): JSX.Element {
  const [on, setOn] = useState(!!defaultChecked);
  const dotColor = tone === "crit" ? "var(--err)" : tone === "warn" ? "var(--warn)" : "var(--ok)";
  return (
    <button type="button" onClick={() => setOn(!on)}
            className="w-full flex items-center justify-between gap-4 px-3 py-2.5 hover:bg-[rgba(31,78,121,0.04)] transition-colors"
            style={{ background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
      <div className="flex items-center gap-2.5">
        {tone && <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />}
        <span className="font-mono" style={{ fontSize: 12, color: "var(--ink-1)" }}>{label}</span>
      </div>
      <span className="relative w-9 h-5 rounded-full transition-colors"
            style={{ background: on ? "var(--accent)" : "#D6DCE4" }}>
        <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
              style={{ left: on ? 18 : 2 }} />
      </span>
    </button>
  );
}
